/* eslint-disable react/jsx-no-useless-fragment */
import { Carer, Home, ResidentItem } from '@lmc/ts-common'
import { Avatar, Chevron, Tag, theme } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
import { GeneralText } from '../styledComponents'
import { EditTooltip } from './EditTooltip'
import { Avatar as AntdAvatar } from 'antd'
import useFeatureFlag from 'hooks/useFeatureFlags'

type GeneralZoneSectionProps = { resident: ResidentItem; hasPro?: boolean }

export const GeneralZoneSection = ({
  resident,
  hasPro,
}: GeneralZoneSectionProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const router = useRouter()

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  const flags = useFeatureFlag({
    flagNames: ['keyworkers'],
  })?.data

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const flagsKeyworkersEnabled = flags?.keyworkers?.enabled

  const keyworkersEnabled =
    flagsKeyworkersEnabled && home?.features?.goals?.enabled

  const hasKeyworkers = !!resident?.keyworkers?.length

  return (
    <CoreSectionWrapper>
      <CoreSection>
        <StyledCoreLabel level={2}>{t(supConstants.ZONE)}</StyledCoreLabel>
        {!hasPro && (
          <Button
            variant="text"
            onClick={() => router.navigate('/settings/zones')}
          >
            {t(supConstants.PRO_FEATURE)}
            <Chevron direction="right" color={theme.color.status.primary} />
          </Button>
        )}
        {hasPro && (
          <GeneralText weight="bold">
            <EditTooltip title={t(supConstants.ZONE_TOOLTIP)}>
              <>{resident?.zoneTitle ? resident?.zoneTitle : 'Unassigned'}</>
            </EditTooltip>
          </GeneralText>
        )}
      </CoreSection>
      <CoreSectionRow>
        {resident?.location?.building ? (
          <CoreSectionInner>
            <StyledCoreLabel level={2}>
              {t(supConstants.BUILDING)}
            </StyledCoreLabel>
            <EditTooltip title={t(supConstants.BUILDING_TOOLTIP)}>
              <StyledTag>{resident.location.building}</StyledTag>
            </EditTooltip>
          </CoreSectionInner>
        ) : null}
        {resident?.location?.floor ? (
          <CoreSectionInner>
            <StyledCoreLabel level={2}>{t(supConstants.FLOOR)}</StyledCoreLabel>
            <EditTooltip title={t(supConstants.FLOOR_TOOLTIP)}>
              <StyledTag>{resident.location.floor}</StyledTag>
            </EditTooltip>
          </CoreSectionInner>
        ) : null}
        {resident?.location?.room ? (
          <CoreSectionInner>
            <StyledCoreLabel level={2}>{t(supConstants.ROOM)}</StyledCoreLabel>
            <EditTooltip title={t(supConstants.ROOM_TOOLTIP)}>
              <StyledTag>{resident.location?.room}</StyledTag>
            </EditTooltip>
          </CoreSectionInner>
        ) : null}
        {resident?.location?.bed ? (
          <CoreSectionInner>
            <StyledCoreLabel level={2}>{t(supConstants.BED)}</StyledCoreLabel>
            <EditTooltip title={t(supConstants.BED_TOOLTIP)}>
              <StyledTag>{resident.location.bed}</StyledTag>
            </EditTooltip>
          </CoreSectionInner>
        ) : null}
      </CoreSectionRow>
      <CoreSection>
        <StyledCoreLabel level={2}>{t(supConstants.CARE_TEAM)}</StyledCoreLabel>
        <GeneralText weight="bold">
          {resident?.assignAll ? (
            <EditTooltip title={t(supConstants.CARE_TEAM_TOOLTIP)}>
              <>{t(supConstants.ALL_CARERS)}</>
            </EditTooltip>
          ) : (
            <EditTooltip title={t(supConstants.CARE_TEAM_TOOLTIP)}>
              <AvatarsWrapper>
                <AntdAvatar.Group maxCount={5} maxStyle={maxStyle}>
                  {resident?.carers?.map((residentCarerId) => (
                    <Avatar
                      key={residentCarerId}
                      url={
                        carers?.find((carer) => carer?.id === residentCarerId)
                          ?.picture as unknown as string
                      }
                    />
                  ))}
                </AntdAvatar.Group>
              </AvatarsWrapper>
            </EditTooltip>
          )}
        </GeneralText>
      </CoreSection>
      {keyworkersEnabled && (
        <CoreSection>
          <StyledCoreLabel level={2}>
            {t(supConstants.KEYWORKERS)}
          </StyledCoreLabel>
          <GeneralText weight="bold">
            {hasKeyworkers ? (
              <EditTooltip title={t(supConstants.KEYWORKERS_TOOLTIP)}>
                <AvatarsWrapper>
                  <AntdAvatar.Group maxCount={5} maxStyle={maxStyle}>
                    {resident?.keyworkers?.map((keyworkerId) => (
                      <Avatar
                        key={keyworkerId}
                        url={
                          carers?.find((carer) => carer?.id === keyworkerId)
                            ?.picture as unknown as string
                        }
                      />
                    ))}
                  </AntdAvatar.Group>
                </AvatarsWrapper>
              </EditTooltip>
            ) : (
              <EditTooltip title={t(supConstants.KEYWORKERS_TOOLTIP)}>
                <>{t(supConstants.NOT_ASSIGNED)}</>
              </EditTooltip>
            )}
          </GeneralText>
        </CoreSection>
      )}
    </CoreSectionWrapper>
  )
}

const StyledCoreLabel = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const StyledTag = styled(Tag)`
  width: fit-content;
`

const CoreSectionWrapper = styled.div`
  border-top: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color7};
  margin-top: 16px;
  padding-top: 16px;
  display: flex;
  justify-content: space-between;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
  }
`

const CoreSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-top: 15px;
  }
`

const CoreSectionRow = styled.div`
  display: flex;
  width: 100%;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-top: 15px;
  }
`

const CoreSectionInner = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 5px;
`

const AvatarsWrapper = styled.div`
  display: flex;
`

const maxStyle = {
  color: theme.color.neutral.color3,
  backgroundColor: theme.color.neutral.color9,
}
